/**
 * ImageLightbox — full-screen click-to-enlarge overlay for a drone photo.
 *
 * Shared across the report surfaces (dashboard LocationDetail, InventoryReport
 * thumbnails, …) so "click the photo to enlarge" behaves identically everywhere
 * (user request 2026-07-16). Fetches the full-resolution /image derivative
 * itself (list views only load the ~320px thumb), shows the thumbnail as an
 * instant placeholder, and closes on backdrop click or Escape.
 */
import { useEffect } from 'react';

import { useAuthImage } from '@/hooks/useAuthImage';
import { buildPhotoUrl, buildPhotoThumbUrl } from '@/api/inventory';

interface ImageLightboxProps {
  photoId: string;
  alt?: string;
  /** Optional caption shown under the image (e.g. the canonical filename). */
  caption?: string | null;
  onClose: () => void;
}

export function ImageLightbox({ photoId, alt = 'Drone photo', caption, onClose }: ImageLightboxProps) {
  const full  = useAuthImage(buildPhotoUrl(photoId));
  const thumb = useAuthImage(buildPhotoThumbUrl(photoId));
  const src = full || thumb;   // show the thumb instantly; swap to full when ready

  // Escape closes; lock body scroll while open.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  return (
    <div className="img-lightbox" onClick={onClose} role="dialog" aria-modal="true" aria-label={alt}>
      <button className="img-lightbox-close" onClick={onClose} aria-label="Close">✕</button>
      <div className="img-lightbox-inner" onClick={(e) => e.stopPropagation()}>
        {src ? (
          <img className="img-lightbox-img" src={src} alt={alt} />
        ) : full === false && thumb === false ? (
          <div className="img-lightbox-msg">Image unavailable</div>
        ) : (
          <div className="img-lightbox-msg">Loading…</div>
        )}
        {caption && <div className="img-lightbox-caption">{caption}</div>}
      </div>
    </div>
  );
}
